import { useState } from 'react';
import { api } from '../../api';
import { t } from '../../i18n';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { useToast } from '../shared/Toast';

type Tab = 'text' | 'emoji' | 'quick_phrase';

const QUICK_PHRASES = ['Zoʻr!', 'Davom eting!', 'Juda chiroyli!', 'Barakalloh!'] as const;
const EMOJIS = ['💚', '❤️', '🌟'] as const;

type Props = {
  open: boolean;
  childId: number;
  childName: string;
  onClose: () => void;
};

export function MessageComposer({ open, childId, childName, onClose }: Props) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('text');
  const [text, setText] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [selectedPhrase, setSelectedPhrase] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setText('');
    setSelectedEmoji(null);
    setSelectedPhrase(null);
  };

  const send = async () => {
    let content: string | null = null;
    if (tab === 'text' && text.trim()) content = text.trim();
    else if (tab === 'emoji' && selectedEmoji) content = selectedEmoji;
    else if (tab === 'quick_phrase' && selectedPhrase) content = selectedPhrase;
    if (!content) return;
    setSubmitting(true);
    try {
      await api.sendMessage(childId, tab, content);
      toast.show('Yuborildi', 'success');
      reset();
      onClose();
    } catch {
      toast.show(t('common.error.network'), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`${childName}ga xabar`}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-1 rounded-md bg-surface-muted p-1 text-[12px]">
          {(['text', 'emoji', 'quick_phrase'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={[
                'flex-1 rounded px-2 py-1.5 transition',
                tab === k ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted',
              ].join(' ')}
            >
              {t(`parent.message.tab.${k === 'quick_phrase' ? 'quick' : k}`)}
            </button>
          ))}
        </div>

        {tab === 'text' ? (
          <div className="flex flex-col gap-1">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={200}
              placeholder="Matn yozing"
              rows={3}
              className="resize-none rounded-md border-hairline border-border-strong bg-surface p-3 text-[14px] outline-none focus:ring-2 focus:ring-info"
            />
            <span className="self-end text-[11px] text-ink-subtle tabular-nums">
              {text.length}/200
            </span>
          </div>
        ) : null}

        {tab === 'emoji' ? (
          <div className="flex justify-center gap-3">
            {EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => setSelectedEmoji(e)}
                className={[
                  'flex h-16 w-16 items-center justify-center rounded-md border-hairline transition active:scale-[0.97]',
                  selectedEmoji === e
                    ? 'border-info bg-info-bg'
                    : 'border-border bg-surface',
                ].join(' ')}
              >
                <span className="text-[40px] leading-none">{e}</span>
              </button>
            ))}
          </div>
        ) : null}

        {tab === 'quick_phrase' ? (
          <div className="flex flex-wrap gap-2">
            {QUICK_PHRASES.map((p) => (
              <button
                key={p}
                onClick={() => setSelectedPhrase(p)}
                className={[
                  'rounded-full px-3 py-2 text-[13px] transition active:scale-[0.97]',
                  selectedPhrase === p
                    ? 'bg-ink text-ink-inverse'
                    : 'bg-surface-muted text-ink',
                ].join(' ')}
              >
                {p}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-col gap-2 pt-2">
          <Button
            disabled={
              submitting ||
              (tab === 'text' && !text.trim()) ||
              (tab === 'emoji' && !selectedEmoji) ||
              (tab === 'quick_phrase' && !selectedPhrase)
            }
            onClick={send}
            fullWidth
          >
            Yuborish
          </Button>
          <Button variant="ghost" disabled={submitting} onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
